// Round 95p·2 (Feb 2026) — Reusable password input with an eye-icon
// toggle to reveal/hide the password. Used by LoginModal (sign-in +
// register) and by ResetPassword. Passes any extra props through to
// the underlying shadcn <Input>.
// Round 95p·6 — Revealed passwords auto-hide after 10 seconds so a
// peek doesn't leave the credential exposed on a shared screen.
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff } from 'lucide-react';

const REVEAL_MS = 10_000;

const PasswordInput = forwardRef(function PasswordInput(
  { className = '', testIdBase = 'password', ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const toggle = () => {
    setVisible(v => {
      const next = !v;
      clearTimer();
      if (next) {
        timerRef.current = setTimeout(() => setVisible(false), REVEAL_MS);
      }
      return next;
    });
  };

  // Clean up the timer when the input unmounts so we never call
  // setState on a torn-down component.
  useEffect(() => clearTimer, []);

  return (
    <div className="relative">
      <Input
        {...rest}
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={`pr-10 ${className}`}
        data-testid={rest['data-testid'] || `${testIdBase}-input`}
      />
      <button
        type="button"
        onClick={toggle}
        tabIndex={-1}
        aria-label={visible ? 'Hide password' : 'Show password (auto-hides in 10s)'}
        title={visible ? 'Hide password' : 'Show password (auto-hides in 10s)'}
        className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`${testIdBase}-toggle-visibility`}
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});

export default PasswordInput;
